/**
 * Utility functions for formatting phone numbers.
 */

/**
 * Normalizes a phone number by stripping non-digit characters and handling country code.
 * If the number has more than 10 digits and starts with '1', the '1' is removed.
 *
 * @param phoneNumber - The phone number string to normalize
 * @returns A string containing only digits, normalized to remove leading 1 if appropriate
 */
function normalizePhoneNumber(phoneNumber: string): string {
  if (!phoneNumber) return '';

  // Strip all non-digit characters
  const digitsOnly = phoneNumber.replace(/\D/g, '');

  // If first digit is 1 and length > 10, remove the 1
  return digitsOnly.length > 10 && digitsOnly.charAt(0) === '1'
    ? digitsOnly.substring(1)
    : digitsOnly;
}

/**
 * Formats a phone number for use in href links (tel:).
 * Returns the phone number in format '+19999999999'.
 *
 * @param phoneNumber - The phone number to format (can be in any format)
 * @returns Formatted phone number string with +1 prefix for href links
 */
export function formatPhoneNumberForHref(phoneNumber: string): string {
  const normalized = normalizePhoneNumber(phoneNumber);
  if (!normalized) return '';

  // Prefix with +1 only if the original number starts with +1 or 1
  // or is 10 digits long.
  if (phoneNumber.startsWith('1') || phoneNumber.startsWith('+1') || normalized.length === 10) {
    return '+1' + normalized;
  }

  // Return original cleaned digits if not 10 digits
  return normalized;
}

/**
 * Formats a phone number for display.
 * Returns the phone number in format '222-222-2222'.
 *
 * @param phoneNumber - The phone number to format (can be in any format)
 * @returns Formatted phone number string with dashes
 */
export function formatPhoneNumberForDisplay(phoneNumber: string): string {
  const normalized = normalizePhoneNumber(phoneNumber);
  if (!normalized) return '';

  // Format as XXX-XXX-XXXX
  if (normalized.length === 10) {
    return `${normalized.substring(0, 3)}-${normalized.substring(3, 6)}-${normalized.substring(6)}`;
  }

  // Return original cleaned digits if not 10 digits
  return normalized;
}
